import PublicLayout from "@/components/PublicLayout";
import { useSEO } from "@/lib/useSEO";

export default function Privacy() {
  useSEO({
    title: "Privacy Policy — InsureGenie India",
    description: "How InsureGenie collects, uses and protects your data as an Indian user. IT Act 2000 aligned and IRDAI-compliant.",
  });
  return (
    <PublicLayout eyebrow="Privacy" title="How we handle your data.">
      <p className="text-muted-foreground"><em>Last updated: February 2026</em></p>

      <p>
        InsureGenie (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is committed to protecting your privacy.
        This policy explains what information we collect, why we collect it, and the choices you have.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account information</strong> — name, email, and (for password sign-up) a securely hashed password. If you sign in with Google, we receive your name, email, and profile picture from Google.</li>
        <li><strong>Profile preferences</strong> — optional fields you choose to share: age, gender, family size, city, pre-existing conditions, smoker status, income bracket, tax-saving priority. These power personalized answers.</li>
        <li><strong>Conversation data</strong> — the questions you ask InsureGenie and the AI responses, stored to your account so you can revisit them.</li>
        <li><strong>Uploaded documents</strong> — insurance brochures and policy PDFs you upload, plus the metadata you attach (insurer name, policy name, type, version, category).</li>
        <li><strong>Contact requests</strong> — name, email, subject, and message when you write to us.</li>
        <li><strong>Technical metadata</strong> — IP address, browser type, and basic usage analytics necessary to operate and secure the service.</li>
      </ul>

      <h2>2. How we use your data</h2>
      <ul>
        <li>To provide AI-generated answers personalized to your profile and uploaded documents.</li>
        <li>To save and restore your conversation history across devices.</li>
        <li>To respond to support and contact requests.</li>
        <li>To detect abuse, prevent fraud, and keep the platform secure.</li>
        <li>To improve InsureGenie&apos;s features and quality in aggregate, never to profile individuals for advertising.</li>
      </ul>

      <h2>3. AI model providers</h2>
      <p>
        AI responses are generated using Anthropic&apos;s Claude Sonnet 4.5 model. Your messages and the
        relevant context from your profile and documents are transmitted to the model provider in real time
        to generate responses. We do not allow the provider to train on your data.
      </p>

      <h2>4. Object storage</h2>
      <p>
        Uploaded PDFs are stored in Emergent Object Storage. Access is gated through our backend — files are
        never exposed via public URLs. When you delete a document in the UI, it&apos;s soft-deleted in our
        database and excluded from all future processing.
      </p>

      <h2>5. Data sharing</h2>
      <p>
        We do <strong>not</strong> sell your data. We do not share it with insurance companies or brokers. The only
        third parties that process data are infrastructure providers strictly necessary to run the service
        (hosting, AI inference, analytics).
      </p>

      <h2>6. Data retention</h2>
      <p>
        Conversation history and profile data are retained until you delete them or close your account.
        Soft-deleted documents are removed from production within 30 days of deletion.
      </p>

      <h2>7. Your rights</h2>
      <ul>
        <li>Access, edit, or delete your profile preferences at any time.</li>
        <li>Delete individual conversations and documents from the app.</li>
        <li>Request a copy of all data we hold about you by writing to <a href="mailto:privacy@insuregenie.ai">privacy@insuregenie.ai</a>.</li>
        <li>Request full account deletion by writing to the same address.</li>
      </ul>

      <h2>8. Security</h2>
      <p>
        Passwords are hashed using bcrypt. Authentication tokens are issued as httpOnly, Secure, SameSite-strict
        cookies. All traffic is encrypted in transit (TLS 1.2+). Access to production data is restricted to a
        minimal engineering team.
      </p>

      <h2>9. Children</h2>
      <p>
        InsureGenie is not intended for users under 18. We do not knowingly collect data from minors.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be highlighted on this page and
        — for registered users — surfaced inside the app before they take effect.
      </p>

      <h2>11. Contact</h2>
      <p>
        For any privacy-related question, reach us at <a href="mailto:privacy@insuregenie.ai">privacy@insuregenie.ai</a>
        {" "}or through our <a href="/contact">contact form</a>.
      </p>
    </PublicLayout>
  );
}
